import type BABYLON from 'babylonjs';

import type { CameraOptions } from './camera';
import { createOrUpdateCamera } from './camera';
import type { EnvironmentOptions } from './environment';
import { createEnvironment, updateEnvironment } from './environment';
import type { Globals } from './globals';
import type { GridOptions } from './grid';
import { createGrid, removeGrid } from './grid';
import type { DirectionalLightOptions, HemisphericLightOptions } from './light';
import { createOrUpdateDirectionalLight, createOrUpdateHemisphericLight } from './light';
import { mergeOptions } from './options';
import type { DefaultRenderingPipelineOptions } from './renderingPipeline';
import { createOrUpdateDefaultRenderingPipeline } from './renderingPipeline';
import type { SceneOptions } from './scene';
import { createOrUpdateScene } from './scene';
import type { SkyboxOptions } from './skybox';
import { createOrUpdateSkybox } from './skybox';

export interface SceneAndEnviromentOptions {
  scene?: SceneOptions;
  camera?: CameraOptions;
  light?: HemisphericLightOptions | null;
  directionalLight?: DirectionalLightOptions | null;
  environment?: EnvironmentOptions | null;
  skybox?: SkyboxOptions | null;
  grid?: GridOptions | null;
  defaultPipeline?: DefaultRenderingPipelineOptions | null;
}

function mergeSceneAndEnviromentOptions(
  nextOptions: SceneAndEnviromentOptions,
  prevOptions: SceneAndEnviromentOptions
): [mergeOptions: SceneAndEnviromentOptions, diffOptions: SceneAndEnviromentOptions] {
  const result: [mergeOptions: SceneAndEnviromentOptions, diffOptions: SceneAndEnviromentOptions] =
    [{}, {}];
  (['scene', 'camera'] as (keyof SceneAndEnviromentOptions)[]).forEach((k) => {
    const r = mergeOptions(nextOptions[k], prevOptions[k]);
    /* eslint-disable @typescript-eslint/no-explicit-any */
    result[0][k] = (r[0] || undefined) as any;
    result[1][k] = (r[1] || undefined) as any;
    /* eslint-enable @typescript-eslint/no-explicit-any */
  });
  (
    [
      'light',
      'directionalLight',
      'environment',
      'skybox',
      'grid',
      'defaultPipeline',
    ] as (keyof SceneAndEnviromentOptions)[]
  ).forEach((k) => {
    const r = mergeOptions(nextOptions[k], prevOptions[k]);
    /* eslint-disable @typescript-eslint/no-explicit-any */
    result[0][k] = r[0] as any;
    result[1][k] = r[1] as any;
    /* eslint-enable @typescript-eslint/no-explicit-any */
  });
  return result;
}

export function createOrUpdateSceneAndEnviroment(
  globals: Globals,
  engine: BABYLON.Engine,
  environmentHelper: BABYLON.Nullable<BABYLON.EnvironmentHelper>,
  nextOptions: SceneAndEnviromentOptions,
  prevOptions?: SceneAndEnviromentOptions
): [
  scene: BABYLON.Scene,
  environmentHelper: BABYLON.Nullable<BABYLON.EnvironmentHelper>,
  options: SceneAndEnviromentOptions
] {
  let nextEnvironmentHelper = environmentHelper;
  const sceneExists = engine.scenes.length > 0;
  const [mergedOptions, options] = mergeSceneAndEnviromentOptions(nextOptions, prevOptions || {});
  const scene = createOrUpdateScene(globals, engine, options.scene);
  if (options.camera !== undefined) createOrUpdateCamera(scene, options.camera);
  if (options.light !== undefined)
    createOrUpdateHemisphericLight(globals, scene, 'hemisphericLight', options.light);
  if (options.directionalLight !== undefined)
    createOrUpdateDirectionalLight(globals, scene, 'directionalLight', options.directionalLight);
  if (options.environment !== undefined) {
    if (!options.environment || options.environment.createGround) {
      nextEnvironmentHelper?.dispose();
      nextEnvironmentHelper = null;
    }
    if (options.environment) {
      const opts = {
        // Do not overwrite imageProcessing because it is set in the defaultPipeline
        setupImageProcessing: !sceneExists,
        ...(!nextEnvironmentHelper ? mergedOptions.environment : options.environment),
      };
      if (nextEnvironmentHelper) {
        updateEnvironment(globals, nextEnvironmentHelper, opts);
      } else {
        nextEnvironmentHelper = createEnvironment(globals, scene, opts);
      }
    }
  }
  const skyboxOptions = options.scene?.environmentTexture ? mergedOptions.skybox : options.skybox;
  if (skyboxOptions !== undefined) createOrUpdateSkybox(scene, skyboxOptions);
  if (options.grid !== undefined) {
    removeGrid(globals, scene);
    if (options.grid) createGrid(globals, scene, options.grid);
  }
  if (options.defaultPipeline !== undefined)
    createOrUpdateDefaultRenderingPipeline(
      globals,
      scene,
      'defaultRenderingPipeline',
      // When switching imageProcessingEnabled, the initial value disappears, so always redefine it
      mergedOptions.defaultPipeline
    );
  return [scene, nextEnvironmentHelper, mergedOptions];
}
